import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  QueueOutlined as QueueIcon
} from '@mui/icons-material';
import { groupPlayAPI, tenantSettingsAPI } from '../../services/api';
import { TierAccessLevel } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import { useRequiredMemberContext } from '../../hooks/useMemberContext';

const GroupPlayBookingPage: React.FC = () => {
  const { groupPlayId, instanceId } = useParams<{ groupPlayId: string; instanceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, memberProfile, isReady } = useRequiredMemberContext();

  const [groupPlayInstance, setGroupPlayInstance] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [cancellationSuccess, setCancellationSuccess] = useState<boolean>(false);
  const [tierAccessLevel, setTierAccessLevel] = useState<TierAccessLevel>(TierAccessLevel.FREE);
  const [cost, setCost] = useState<number | null>(null);
  const [existingBooking, setExistingBooking] = useState<any | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);

  // Cancellation window state
  const [cancellationWindowHours, setCancellationWindowHours] = useState(24);
  const [hoursUntilBooking, setHoursUntilBooking] = useState<number | null>(null);
  const [canCancelWithoutPenalty, setCanCancelWithoutPenalty] = useState(false);

  // Get referrer for breadcrumbs
  const referrer = (location.state as any)?.from || 'calendar';

  useEffect(() => {
    if (groupPlayId && instanceId && isReady && memberProfile?.id) {
      fetchGroupPlayInstance();
      fetchTenantSettings();
    }
  }, [groupPlayId, instanceId, isReady, memberProfile?.id]);

  // Calculate hours until booking
  useEffect(() => {
    if (groupPlayInstance) {
      calculateHoursUntilBooking();
    }
  }, [groupPlayInstance, cancellationWindowHours]);

  const fetchTenantSettings = async () => {
    try {
      const response = await tenantSettingsAPI.getAllSettings();
      const settings = response.data;

      // Get cancellation window from tenant settings
      const cancelHours = settings.cancelWithoutPenaltyHrs || 24;
      setCancellationWindowHours(cancelHours);
    } catch (error: any) {
      console.error('Error fetching tenant settings:', error);
      // Use default if fetch fails
      setCancellationWindowHours(24);
    }
  };

  const calculateHoursUntilBooking = () => {
    if (!groupPlayInstance) return;

    try {
      const dateStr = groupPlayInstance.sessionDate;
      const timeStr = groupPlayInstance.startTime;

      if (!dateStr || !timeStr) {
        setHoursUntilBooking(null);
        setCanCancelWithoutPenalty(false);
        return;
      }

      // Parse the date and time - match AmenityBookingDetailPage pattern
      const [year, month, day] = dateStr.split('-').map(Number);
      const cleanTime = timeStr.split('+')[0].split('-')[0]; // Strip timezone
      const [hours, minutes] = cleanTime.split(':').map(Number);

      // Create booking date in local timezone
      const bookingDate = new Date(year, month - 1, day, hours, minutes, 0);
      const now = new Date();

      // Calculate difference in milliseconds
      const diffMs = bookingDate.getTime() - now.getTime();

      // Convert to hours
      const diffHours = diffMs / (1000 * 60 * 60);

      setHoursUntilBooking(diffHours);
      setCanCancelWithoutPenalty(diffHours >= cancellationWindowHours);
    } catch (error) {
      console.error('Error calculating hours until booking:', error);
      setHoursUntilBooking(null);
      setCanCancelWithoutPenalty(false);
    }
  };

  const fetchGroupPlayInstance = async () => {
    if (!groupPlayId || !instanceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await groupPlayAPI.getGroupPlayInstanceById(groupPlayId, instanceId);
      const instance = response.data;
      setGroupPlayInstance(instance);

      // Check if current member profile already has a booking for this instance
      // This would need to be added to the API response or fetched separately
      // For now, we'll use user bookings endpoint
      const bookingsResponse = await groupPlayAPI.getUserBookings();
      const userBooking = bookingsResponse.data.find(
        (b: any) => b.groupPlayInstanceId === instanceId && (b.status === 'CONFIRMED' || b.status === 'WAITLIST')
      );
      setExistingBooking(userBooking || null);

      // Determine tier access level and cost
      if (memberProfile?.membershipTier) {
        const tierName = memberProfile.membershipTier.name;
        let accessLevel = TierAccessLevel.FREE; // Default to free

        if (instance.tierAccess && tierName) {
          const configuredAccess = instance.tierAccess[tierName];
          if (configuredAccess) {
            accessLevel = configuredAccess as TierAccessLevel;
          }
        } else if (instance.parentGroupPlay?.tierAccess && tierName) {
          const configuredAccess = instance.parentGroupPlay.tierAccess[tierName];
          if (configuredAccess) {
            accessLevel = configuredAccess as TierAccessLevel;
          }
        }

        setTierAccessLevel(accessLevel);

        // Set cost - use paidAmount from the booking (which reflects the transaction amount)
        // The 'paid' field indicates if the transaction is completed, but paidAmount shows what will be charged
        if (userBooking && userBooking.paidAmount && userBooking.paidAmount > 0) {
          setCost(Number(userBooking.paidAmount));
        } else {
          setCost(null);
        }
      }
    } catch (error: any) {
      console.error('Error fetching group play instance:', error);
      setError(error.response?.data?.message || 'Failed to load group play details');
    } finally {
      setLoading(false);
    }
  };

  const handleBookClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmBooking = async () => {
    if (!groupPlayInstance || !memberProfile || !groupPlayId || !instanceId) return;

    setBookingLoading(true);
    setError(null);

    try {
      // Don't pass memberProfileId for self-booking - backend uses x-member-profile-id header
      const response = await groupPlayAPI.bookGroupPlayInstance(groupPlayId, instanceId, {
        notes: ''
      });

      setBookingSuccess(true);
      setOpenConfirmDialog(false);

      // Close success message and redirect after 2 seconds
      setTimeout(() => {
        navigate('/dashboard', { state: { from: 'group-play-booking' } });
      }, 2000);
    } catch (error: any) {
      console.error('Error booking group play:', error);
      setError(error.response?.data?.message || 'Failed to book group play session');
      setOpenConfirmDialog(false);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleCancelBookingClick = async () => {
    if (!existingBooking) return;
    setCancelDialogOpen(true);
  };

  const handleConfirmCancellation = async () => {
    if (!existingBooking || !groupPlayId || !instanceId) return;

    setCancelLoading(true);
    setError(null);

    try {
      // No need to pass overrideCancellationWindow - backend handles transaction logic
      await groupPlayAPI.cancelGroupPlayInstanceBooking(groupPlayId, instanceId, existingBooking.id, {});

      // Show success message
      setCancellationSuccess(true);
      setCancelDialogOpen(false);
      setExistingBooking(null);

      // Redirect to dashboard after successful cancellation
      setTimeout(() => {
        navigate('/dashboard', { state: { from: 'group-play-booking' } });
      }, 3000);
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      setError(error.response?.data?.message || 'Failed to cancel booking');
      setCancelDialogOpen(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !groupPlayInstance) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!groupPlayInstance) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Group play session not found
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const isFullyBooked = (groupPlayInstance.confirmedCount || 0) >= groupPlayInstance.capacity;
  const isWaitlistAvailable = groupPlayInstance.isWaitlistAvailable && isFullyBooked;
  const canBook = (!isFullyBooked || isWaitlistAvailable) && tierAccessLevel !== TierAccessLevel.NO_ACCESS && !existingBooking;
  const hasExistingBooking = !!existingBooking;
  const isOnWaitlist = existingBooking && existingBooking.status === 'WAITLIST';

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Success Message */}
        {bookingSuccess && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
          >
            {isWaitlistAvailable
              ? 'Successfully joined the waitlist! Redirecting to dashboard...'
              : 'Successfully booked! Redirecting to dashboard...'}
          </Alert>
        )}

        {/* Cancellation Success Message */}
        {cancellationSuccess && (
          <Alert
            severity="info"
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
          >
            Booking cancelled successfully. Redirecting to dashboard...
          </Alert>
        )}

        {/* Existing Booking Alert */}
        {hasExistingBooking && !cancellationSuccess && (
          <Alert severity={isOnWaitlist ? "warning" : "info"} sx={{ mb: 3 }}>
            {isOnWaitlist
              ? "You are currently on the waitlist for this session."
              : "You already have a booking for this session."}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Main Content */}
        <Paper sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {groupPlayInstance.name}
            </Typography>
            {groupPlayInstance.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {groupPlayInstance.description}
              </Typography>
            )}
          </Box>

          {/* Actions - Moved to top for mobile UX */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={bookingLoading || cancelLoading}
              sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: 120 } }}
            >
              Back
            </Button>
            {hasExistingBooking ? (
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={handleCancelBookingClick}
                disabled={cancelLoading || cancellationSuccess}
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: 150 } }}
              >
                {cancelLoading ? 'Cancelling...' : isOnWaitlist ? 'Leave Waitlist' : 'Cancel Booking'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={handleBookClick}
                disabled={!canBook || bookingLoading || bookingSuccess}
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: 150 } }}
              >
                {isFullyBooked && !isWaitlistAvailable
                  ? 'Session Full'
                  : isWaitlistAvailable
                    ? (bookingLoading ? 'Joining...' : 'Join Waitlist')
                    : (bookingLoading ? 'Booking...' : 'Book')}
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Session Details */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <List disablePadding>
                {/* Date & Time - Combined */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                      <CalendarIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Date</Typography>
                        <Typography variant="body2" color="text.secondary">{formatDate(groupPlayInstance.sessionDate)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                      <ScheduleIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimeString(groupPlayInstance.startTime)} - {formatTimeString(groupPlayInstance.endTime)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>

                {/* Type */}
                {groupPlayInstance.parentGroupPlay?.type && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <EventIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Type</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {groupPlayInstance.parentGroupPlay.type.charAt(0).toUpperCase() + groupPlayInstance.parentGroupPlay.type.slice(1)}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                )}
                {/* Venues */}
                {groupPlayInstance.venues && groupPlayInstance.venues.length > 0 && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <LocationIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Venues</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {groupPlayInstance.venues.length} venue(s) available
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                )}



                {/* Capacity */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PersonIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Capacity</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {groupPlayInstance.confirmedCount || 0} / {groupPlayInstance.capacity}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={isFullyBooked ? 'Full' : 'Available'}
                        color={isFullyBooked ? 'error' : 'success'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </ListItem>

                {/* Waitlist */}
                {groupPlayInstance.isWaitlistAvailable && groupPlayInstance.waitlistCount > 0 && (
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1, border: 1, borderColor: 'warning.light' }}>
                      <QueueIcon sx={{ mr: 2, color: 'warning.main', fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>Waitlist</Typography>
                        <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 'medium' }}>
                          {groupPlayInstance.waitlistCount || 0} on waitlist
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                )}
              </List>
            </Grid>
          </Grid>

          {/* Price Display */}
          {cost !== null && cost > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Session Fee
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                ${typeof cost === 'number' ? cost.toFixed(2) : Number(cost).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Payment will be processed after booking
              </Typography>
            </Box>
          )}

          {/* Access Level Warning */}
          {tierAccessLevel === TierAccessLevel.NO_ACCESS && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Your membership tier does not have access to this session. Please contact the front desk for more information.
            </Alert>
          )}

          {/* Booking Summary */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Booking For
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {memberProfile?.firstName} {memberProfile?.lastName}
            </Typography>
            {memberProfile?.email && (
              <Typography variant="body2" color="text.secondary">
                {memberProfile.email}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Cancellation Confirmation Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={handleCloseCancelDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Cancel Booking
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
              Are you sure you want to cancel your {isOnWaitlist ? 'waitlist position' : 'booking'} for this session?
            </Typography>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Session:</strong> {groupPlayInstance.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {formatDate(groupPlayInstance.sessionDate)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Time:</strong> {formatTimeString(groupPlayInstance.startTime)} - {formatTimeString(groupPlayInstance.endTime)}
              </Typography>
              {hoursUntilBooking !== null && (
                <Typography variant="body2" gutterBottom>
                  <strong>Hours Until Session:</strong> {hoursUntilBooking.toFixed(1)}
                </Typography>
              )}
              {isOnWaitlist && (
                <Typography variant="body2" gutterBottom>
                  <strong>Status:</strong> On Waitlist
                </Typography>
              )}
            </Box>

            {/* Cancellation Window Warning */}
            {!canCancelWithoutPenalty && hoursUntilBooking !== null && hoursUntilBooking > 0 && cost !== null && cost > 0 && !isOnWaitlist && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Cancellation Fee Will Apply
                </Typography>
                <Typography variant="body2">
                  This cancellation is within {cancellationWindowHours} hours of the session start.
                  A charge of ${cost.toFixed(2)} will be applied to your account.
                </Typography>
              </Alert>
            )}

            {/* Free Cancellation Notice */}
            {canCancelWithoutPenalty && cost !== null && cost > 0 && !isOnWaitlist && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This cancellation is more than {cancellationWindowHours} hours before the session.
                  No charge will be applied.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCancelDialog} disabled={cancelLoading}>
              Keep Booking
            </Button>
            <Button
              onClick={handleConfirmCancellation}
              variant="contained"
              color="error"
              disabled={cancelLoading}
              startIcon={cancelLoading ? <CircularProgress size={20} /> : null}
            >
              {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Booking Confirmation Dialog */}
        <Dialog
          open={openConfirmDialog}
          onClose={handleCancelDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isWaitlistAvailable ? 'Join Waitlist' : 'Confirm Booking'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {isWaitlistAvailable
                ? 'This session is currently full. Would you like to join the waitlist?'
                : 'Are you sure you want to book this session?'}
            </Typography>
            {isWaitlistAvailable && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                You will be automatically moved to confirmed status if a spot becomes available.
              </Alert>
            )}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Session:</strong> {groupPlayInstance.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {formatDate(groupPlayInstance.sessionDate)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Time:</strong> {formatTimeString(groupPlayInstance.startTime)} - {formatTimeString(groupPlayInstance.endTime)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Member:</strong> {memberProfile?.firstName} {memberProfile?.lastName}
              </Typography>
              {cost !== null && cost > 0 && !isWaitlistAvailable && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  <strong>Amount:</strong> ${cost.toFixed(2)}
                </Typography>
              )}
            </Box>
            {cost !== null && cost > 0 && !isWaitlistAvailable && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A charge of ${cost.toFixed(2)} will be applied to your account.
              </Alert>
            )}
            {cost !== null && cost > 0 && isWaitlistAvailable && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A charge of ${cost.toFixed(2)} will be applied when you are confirmed from the waitlist.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialog} disabled={bookingLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBooking}
              variant="contained"
              disabled={bookingLoading}
              startIcon={bookingLoading ? <CircularProgress size={20} /> : null}
            >
              {bookingLoading ? (isWaitlistAvailable ? 'Joining...' : 'Booking...') : (isWaitlistAvailable ? 'Join Waitlist' : 'Confirm Booking')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default GroupPlayBookingPage;
